import classNames from "classnames";
import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate, useSearchParams } from "react-router-dom";
import { mergeClass } from "../utils";
import apiAgent from "../api/apiAgent";
import _ from "lodash";
import { IPublishState } from "../components/Publishing/atoms";
import { Tooltip } from "../components/Tooltip";

const navlinkTextClass =
  "transition py-3 pl-10 pr-5 font-bold hover:bg-[#444444]";

const NavbarStaticText = ({ children }: { children: React.ReactNode }) => (
  <div className="py-2 px-10 text-[#C9C9C9] text-xs">{children}</div>
);

interface INavbarLinkProps {
  to: string;
  children?: string | React.ReactNode;
  className?: string;
}

const NavbarLink = ({ to, children, className }: INavbarLinkProps) => (
  <NavLink
    to={to}
    className={({ isActive }) =>
      mergeClass(
        navlinkTextClass,
        {
          "text-black bg-[#eeede8]": isActive,
        },
        className
      )
    }
  >
    {children}
  </NavLink>
);

const SlideNavbarLink = ({
  screenId,
  children,
}: {
  screenId: number;
  children: string | React.ReactNode;
}) => {
  const [searchParams] = useSearchParams();
  const currentId = _.toNumber(searchParams.get("screenType") || -1);
  const isActive = currentId === screenId;
  return (
    <NavLink
      to={`/m/slides?screenType=${screenId}`}
      className={mergeClass(navlinkTextClass, {
        "text-black bg-[#eeede8] hover:bg-[#bdbdb8]": isActive,
        "hover:bg-[#444444]": !isActive,
      })}
    >
      {children}
    </NavLink>
  );
};

const LogoutButton = () => {
  return (
    <NavLink to="/logout" className={"w-full"}>
      <button
        className={mergeClass(
          `${navlinkTextClass} text-center pb-6 pt-3 font-medium w-full`
        )}
      >
        <i className="icon-logout text-xs" /> Log Out
      </button>
    </NavLink>
  );
};

// TODO: Navbar dropdown
const NavbarDropdown = ({
  label,
  children,
  className,
}: {
  className?: string;
  label: string | React.ReactNode;
  children: React.ReactNode;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuHeight, setMenuHeight] = useState(0);

  useEffect(() => {
    const ht = document
      .getElementById(`${label}_dropdown`)
      ?.getBoundingClientRect().height;
    setMenuHeight(ht || 180);
  }, []);

  return (
    <>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={mergeClass(
          navlinkTextClass,
          "flex justify-between",
          {
            "bg-white/10": isOpen,
          },
          className
        )}
      >
        <div>{label}</div>
        <i
          className={mergeClass(
            "transition icon-back_arrow -rotate-90 place-self-center",
            { "rotate-90": isOpen }
          )}
        />
      </button>
      <div
        className={mergeClass(
          "transition-height bg-white/20 overflow-auto origin-top min-h-fit"
        )}
        style={
          isOpen
            ? {
                height: `${menuHeight}px`,
              }
            : { height: "0px" }
        }
      >
        <div id={`${label}_dropdown`} className="flex flex-col min-h-fit">
          {children}
        </div>
      </div>
    </>
  );
};

const NavbarTextWithIndicator = ({
  publishObj,
  children,
}: {
  children: string;
  publishObj: IPublishState;
}) => (
  <div className="relative">
    {publishObj.lastchange === publishObj.lastpublish || (
      <div
        className={mergeClass(
          "transition h-2 w-2 rounded-full bg-warningOrange group/tooltip group-hover/tooltip",
          "absolute right-[calc(100%+0.5rem)] inset-y-[calc(50%-0.25rem)] hover:ring"
        )}
      >
        <Tooltip>Unpublished changes</Tooltip>
      </div>
    )}
    {children}
  </div>
);

export {
  NavbarLink,
  NavbarDropdown,
  NavbarStaticText,
  LogoutButton,
  SlideNavbarLink,
  NavbarTextWithIndicator,
};
